import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./DateV2Section.module.css";
import DatePicker from "Events/Components/DatePicker";
import TimePicker from "Events/Components/TimePicker";
import { Select } from "shared/Select/Select";
import moment from "moment-timezone";
import {
  buildInitialDateState,
  buildUtcFromLocalParts,
  getTimezoneFieldForDateField,
  rebuildDatesForTimezoneChange,
  utcToLocalDateTimeDisplay,
} from "shared/dateTimeHelpers";

export function DateV2Section({
  dateInfo,
  location,
  ruleset,
  eventName,
  onDateTimeChange,
  pageMode,
  section,
  timezonePermission,
  onlyShowDateKey,
  isLocal2 = false,
}) {
  const [dateInfoData, setDateInfoData] = useState({
    purchaseStartDate: "",
    purchaseEndDate: "",
    eventStartDate: "",
    eventEndDate: "",
    eagleStartDate: "",
    eagleEndDate: "",
    checkInStartDate: "",
    checkInEndDate: "",
    startFeeDate: "",
    changeableUntilDate: "",
    visibleOnCalendarDate: "",
  });

  const [timeZoneData, setTimeZoneData] = useState({
    purchaseStartTimeZone: "Pacific Time (PT)",
    eventStartTimeZone: "Pacific Time (PT)",
    eagleStartTimeZone: "Pacific Time (PT)",
    checkInStartTimeZone: "Pacific Time (PT)",
    startFeeTimeZone: "Pacific Time (PT)",
    changeableUntilTimeZone: "Pacific Time (PT)",
    visibleOnCalendarTimeZone: "Pacific Time (PT)",
  });

  const pendingTimezoneChangeRef = useRef(null);

  const getTimeZoneAbbr = (timeZone) => {
    switch (timeZone) {
      case "America/Los_Angeles":
        return "Pacific Time (PT)";
      case "America/Boise":
        return "Mountain Time (MT)";
      case "America/Chicago":
        return "Central Time (CT)";
      case "America/New_York":
        return "Eastern Time (ET)";
      case "America/Anchorage":
        return "Alaska Time (AKT)";
      case "America/Adak":
        return "Hawaii-Aleutian Time (HAT)";
      case "Pacific/Honolulu":
        return "Hawaii-Honolulu (HST)";
      case "Australia/Sydney":
        return "Australian Eastern Standard Time (AEST)";
      case "Australia/Adelaide":
        return "Australian Central Standard Time (ACST)";
      case "Australia/Perth":
        return "Australian Western Standard Time (AWST)";
      case "Pacific/Auckland":
        return "New Zealand Standard Time (NZST)";
      default:
        return timeZone || "Pacific Time (PT)";
    }
  };

  const timeZones = [
    "Pacific Time (PT)",
    "Mountain Time (MT)",
    "Central Time (CT)",
    "Eastern Time (ET)",
    "Alaska Time (AKT)",
    "Hawaii-Aleutian Time (HAT)",
    "Hawaii-Honolulu (HST)",
    "Australian Eastern Standard Time (AEST)",
    "Australian Central Standard Time (ACST)",
    "Australian Western Standard Time (AWST)",
    "New Zealand Standard Time (NZST)",
    "Australian Eastern Daylight Time (AEDT)",
    "New Zealand Daylight Time (NZDT)",
  ];

  const shouldRenderTimeZoneSelect = (dateField) => {
    const timezoneField = getTimezoneFieldForDateField({
      dateField,
      ruleset,
      isLocal2,
    });

    if (timezoneField === "eventStartTimeZone") {
      return dateField === "eventStartDate";
    }

    if (timezoneField === "purchaseStartTimeZone") {
      return dateField === "purchaseStartDate";
    }

    if (timezoneField === "visibleOnCalendarTimeZone") {
      return dateField === "visibleOnCalendarDate";
    }

    if (timezoneField === "eagleStartTimeZone") {
      return dateField === "eagleStartDate";
    }

    if (timezoneField === "checkInStartTimeZone") {
      return dateField === "checkInStartDate";
    }

    if (timezoneField === "startFeeTimeZone") {
      return dateField === "startFeeDate";
    }

    if (timezoneField === "changeableUntilTimeZone") {
      return dateField === "changeableUntilDate";
    }

    return false;
  };

  useEffect(() => {
    if (!dateInfo?.dates) return;

    const pending = pendingTimezoneChangeRef.current;

    if (pending) {
      const upstreamTimezone =
        dateInfo.dates?.[pending.timezoneField]?.saved ||
        dateInfo.dates?.[pending.timezoneField]?.value;

      if (upstreamTimezone !== pending.selectedOption) {
        return;
      }

      pendingTimezoneChangeRef.current = null;
    }

    // Always rebuild state when dateInfo changes or timezone change completes
    const { dateInfoData: nextDateInfoData, timeZoneData: nextTimeZoneData } =
      buildInitialDateState({
        dates: dateInfo.dates,
        ruleset,
        isLocal2,
      });

    setDateInfoData(nextDateInfoData);
    setTimeZoneData(nextTimeZoneData);
  }, [dateInfo?.dates, ruleset, isLocal2]);

  const handleDateTimeChange = (key, value, isTime = false) => {
    setDateInfoData((prevData) => {
      const newData = { ...prevData };
      const timeKey = key.replace("Date", "Time");

      const timezoneField = getTimezoneFieldForDateField({
        dateField: key,
        ruleset,
        isLocal2,
      });

      const selectedTimezone =
        timeZoneData[timezoneField] ||
        dateInfo?.dates?.[timezoneField]?.saved ||
        dateInfo?.dates?.[timezoneField]?.value;

      if (isTime) {
        let existingDate = prevData[key];

        if (
          !existingDate ||
          !moment(existingDate, "MM/DD/YYYY", true).isValid()
        ) {
          existingDate = moment().format("MM/DD/YYYY");
          newData[key] = existingDate;
        }

        const rebuiltUtc = buildUtcFromLocalParts({
          localDate: existingDate,
          localTime: value,
          timeZone: selectedTimezone,
        });

        if (rebuiltUtc) {
          onDateTimeChange(key, rebuiltUtc, false);
        }

        newData[timeKey] = value;
        return newData;
      }

      const existingTime = prevData[timeKey] || "00:00";

      if (!moment(value, "MM/DD/YYYY", true).isValid()) {
        return prevData;
      }

      const rebuiltUtc = buildUtcFromLocalParts({
        localDate: value,
        localTime: existingTime,
        timeZone: selectedTimezone,
      });

      if (rebuiltUtc) {
        onDateTimeChange(key, rebuiltUtc, false);
      }

      newData[key] = value;
      return newData;
    });
  };

  const handleTimeZoneChange = (timezoneField, selectedOption) => {
    pendingTimezoneChangeRef.current = {
      timezoneField,
      selectedOption,
    };

    const rebuiltDates = rebuildDatesForTimezoneChange({
      timezoneField,
      nextTimezone: selectedOption,
      dateInfoData,
      ruleset,
      isLocal2,
    });

    setTimeZoneData((prev) => ({
      ...prev,
      [timezoneField]: selectedOption,
    }));

    setDateInfoData((prevData) => {
      const nextData = { ...prevData };

      Object.keys(rebuiltDates).forEach((dateField) => {
        const timeField = dateField.replace("Date", "Time");
        nextData[dateField] = prevData[dateField];
        nextData[timeField] = prevData[timeField] || "";
      });

      return nextData;
    });

    onDateTimeChange(
      {
        [timezoneField]: selectedOption,
        ...rebuiltDates,
      },
      null,
      false
    );
  };

  const renderPickerField = (fieldKey, label, fallbackDate = null) => {
    const isTimeField = fieldKey.endsWith("Time");
    const permissionKey = isTimeField
      ? fieldKey.replace("Time", "Date")
      : fieldKey;

    const fieldTimeZoneKey = getTimezoneFieldForDateField({
      dateField: permissionKey,
      ruleset,
      isLocal2,
    });

    const fieldTimeZone =
      timeZoneData[fieldTimeZoneKey] ||
      dateInfo?.dates?.[fieldTimeZoneKey]?.saved ||
      dateInfo?.dates?.[fieldTimeZoneKey]?.value;

    const localDateValue = dateInfoData[permissionKey] || "";
    const localTimeValue =
      dateInfoData[permissionKey.replace("Date", "Time")] || "";

    const rawUtcValue =
      dateInfo?.dates?.[permissionKey]?.saved ||
      dateInfo?.dates?.[permissionKey]?.value ||
      null;

    const datePermission =
      pageMode === "view"
        ? "read"
        : dateInfo?.dates?.[permissionKey]?.permission;

    const effectiveDatePermission =
      isLocal2 &&
      section === "Event Information" &&
      permissionKey === "eventEndDate" &&
      datePermission !== "hidden"
        ? "write"
        : datePermission;

    const isCheckInDate =
      fieldKey === "checkInStartDate" || fieldKey === "checkInEndDate";

    const eventEndDate = dateInfoData.eventEndDate;

    const dateKey = `${fieldKey}-date-${localDateValue || "null"}`;
    const timeKey = `${fieldKey}-time-${localTimeValue || "null"}`;
    const refreshKey = `${fieldKey}-${fallbackDate}`;

    if (effectiveDatePermission === "read" && pageMode === "view") {
      const displayValue = rawUtcValue
        ? utcToLocalDateTimeDisplay(rawUtcValue, fieldTimeZone)
        : "N/A";

      return (
        <div>
          <span className={styles.dateText}>{displayValue}</span>
        </div>
      );
    }

    if (effectiveDatePermission === "write") {
      if (isTimeField) {
        return null;
      }

      return (
        <div className={styles.labelAndPickerContainer} key={refreshKey}>
          <DatePicker
            key={dateKey}
            dateString={localDateValue}
            fallbackDate={fallbackDate || moment().format("MM/DD/YYYY")}
            setDate={(date) => {
              handleDateTimeChange(fieldKey, date, false);
            }}
            eventEndDate={isCheckInDate ? eventEndDate : undefined}
          />

          <TimePicker
            key={timeKey}
            timeString={localTimeValue}
            onChange={(time) => {
              handleDateTimeChange(fieldKey, time, true);
            }}
          />
        </div>
      );
    }

    if (effectiveDatePermission === "hidden") {
      return null;
    }

    return null;
  };

  const renderTimeZoneSelect = (fieldKey, label) => {
    const dateKey = `${fieldKey}Date`;

    const timezoneKey = getTimezoneFieldForDateField({
      dateField: dateKey,
      ruleset,
      isLocal2,
    });

    const datePermission =
      pageMode === "view" || timezonePermission === "view"
        ? "read"
        : dateInfo?.dates?.[timezoneKey]?.permission;

    const timezoneValue = timeZoneData[timezoneKey] || "Pacific Time (PT)";

    if (
      pageMode === "view" &&
      (fieldKey === "startFee" || fieldKey === "changeableUntil")
    ) {
      return null;
    }

    if (timezonePermission === "read") {
      const dateObj = dateInfo?.dates?.[dateKey];
      const dateExists =
        dateObj && (dateObj.saved != null || dateObj.value != null);

      if (!dateExists) {
        return null;
      }

      return (
        <div className={styles.timezoneText}>
          {getTimeZoneAbbr(
            dateInfo?.dates?.[timezoneKey]?.saved ||
              dateInfo?.dates?.[timezoneKey]?.value ||
              timezoneValue
          )}
        </div>
      );
    }

    if (datePermission === "write") {
      return (
        <div className={styles.timezone}>
          <Select
            options={timeZones}
            value={timezoneValue}
            placeholder={timezoneValue}
            onChange={(selectedOption) =>
              handleTimeZoneChange(timezoneKey, selectedOption)
            }
            disable={false}
            className={styles.timezone}
          />
        </div>
      );
    }

    if (datePermission === "hidden") {
      return null;
    }

    return null;
  };

  const splitLabel = (label) => {
    if (label.includes("Starts On")) {
      return [label.replace(" Starts On", ""), "Starts On:"];
    } else if (label.includes("Ends On")) {
      return [label.replace(" Ends On", ""), "Ends On:"];
    }
    return [label];
  };

  const renderDateTimeSet = (
    startDateKey,
    startTimeKey,
    startLabel,
    endDateKey,
    endTimeKey,
    endLabel
  ) => {
    const [startDateLabel, suffixLabel1] = splitLabel(startLabel);
    const [endDateLabel, suffixLabel2] = endLabel ? splitLabel(endLabel) : [];

    const isRequired = dateInfo.dates[startDateKey]?.required;
    const startOnDate = dateInfoData[startDateKey];
    const endOnDate = dateInfoData[endDateKey] || startOnDate;

    return (
      <div className={styles.dateTimeSet}>
        <div className={styles.rowContainer}>
          <div
            className={`${styles.labelContainer} ${
              !suffixLabel1 ? styles.noSuffixMargin : ""
            }`}
          >
            <label>
              {startDateLabel === "Check In"
                ? "QR Code Display"
                : startDateLabel === "Start Fee"
                ? "Cancel Fee"
                : startDateLabel === "Event"
                ? "Event Date"
                : startDateLabel === "Purchase Begin"
                ? "Tickets on Sale"
                : startDateLabel}
              {isRequired && pageMode === "edit" ? " *" : ""}
            </label>
          </div>

          {suffixLabel1 && (
            <div className={styles.suffixLabelContainer}>
              <label>{suffixLabel1}</label>
            </div>
          )}

          <div className={styles.labelAndPickerContainer}>
            <div className={styles.datePickerContainer}>
              {renderPickerField(
                startDateKey,
                startLabel,
                endDateKey ? dateInfoData[endDateKey] : null
              )}
            </div>

            {pageMode === "edit" && (
              <div className={styles.timePickerContainer}></div>
            )}

            {dateInfo.dates && shouldRenderTimeZoneSelect(startDateKey) && (
              <div className={styles.timezoneEdit}>
                {renderTimeZoneSelect(
                  startDateKey.replace("Date", ""),
                  `${startLabel} Timezone`
                )}
              </div>
            )}
          </div>
        </div>

        {endDateKey && endTimeKey && endLabel && (
          <div className={styles.rowContainer}>
            <div className={styles.labelContainer}>
              <label className={styles.hiddenLabel}>
                <label>{endDateLabel}</label>
                {pageMode === "edit"}
              </label>
            </div>

            {suffixLabel2 && (
              <div className={styles.suffixLabelContainer}>
                <label>{suffixLabel2}</label>
              </div>
            )}

            <div className={styles.labelAndPickerContainer}>
              <div className={styles.datePickerContainer}>
                {renderPickerField(endDateKey, endLabel, startOnDate)}
              </div>

              {pageMode === "edit" && (
                <div className={styles.timePickerContainer}></div>
              )}

              <div className={styles.timezoneEdit}></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const dateKeysToHide = [
    {
      hideKeys: () => section === "Event Information",
      keysToHide: [
        "purchaseStartDate",
        "purchaseEndDate",
        "visibleOnCalendarDate",
        "startFeeDate",
        "changeableUntilDate",
      ],
    },
    {
      hideKeys: () => section === "Publishing Information",
      keysToHide: [
        "eventStartDate",
        "eventEndDate",
        "eagleStartDate",
        "eagleEndDate",
        "checkInStartDate",
        "checkInEndDate",
      ],
    },
    {
      hideKeys: () =>
        eventName !== "Regional" &&
        location === "virtual" &&
        section === "Event Information",
      keysToHide: [
        "eagleStartDate",
        "eagleEndDate",
        "checkInStartDate",
        "checkInEndDate",
      ],
    },
    {
      hideKeys: () => ruleset === "Local v2.0",
      keysToHide: ["startFeeDate", "changeableUntilDate"],
    },
  ];

  let allKeysToHide = [];

  dateKeysToHide.forEach((dateKey) => {
    if (dateKey.hideKeys()) {
      allKeysToHide = allKeysToHide.concat(dateKey.keysToHide);

      const timeZoneKeys = dateKey.keysToHide.map((key) =>
        getTimezoneFieldForDateField({
          dateField: key,
          ruleset,
          isLocal2,
        })
      );

      allKeysToHide = allKeysToHide.concat(timeZoneKeys);
    }
  });

  const filteredDates = Object.keys(dateInfo.dates)
    .filter((key) => key.endsWith("Date"))
    .reduce((acc, key) => {
      const timeZoneKey = getTimezoneFieldForDateField({
        dateField: key,
        ruleset,
        isLocal2,
      });

      if (allKeysToHide.includes(key)) {
        return acc;
      }

      const dateField = dateInfo.dates[key];
      const isLocal2EventEndDateFallback =
        isLocal2 &&
        section === "Event Information" &&
        key === "eventEndDate" &&
        dateField?.field === null &&
        dateField?.permission !== "hidden";

      if (
        (dateField?.field !== null && dateField?.permission !== "hidden") ||
        isLocal2EventEndDateFallback
      ) {
        if (isLocal2EventEndDateFallback) {
          const startField = dateInfo.dates?.eventStartDate?.field;
          acc[key] =
            (startField &&
              startField
                .replace("Starts On", "Ends On")
                .replace("Start On", "Ends On")) ||
            "Dates > Event Date > Ends On";
        } else {
          acc[key] = dateField.field;
        }

        const timeZoneField = dateInfo.dates[timeZoneKey];

        if (
          timeZoneField &&
          !allKeysToHide.includes(timeZoneKey) &&
          timeZoneField?.permission !== "hidden"
        ) {
          acc[timeZoneKey] = timeZoneField.field;
        }
      }

      return acc;
    }, {});

  const extractLabels = (dates) => {
    const labels = {};

    Object.keys(dates).forEach((key) => {
      const labelPath = dates[key];

      if (labelPath) {
        let baseName = labelPath.replace(/^Dates\s*>\s*/, "").trim();

        let suffix = "";
        if (key.endsWith("StartDate")) {
          suffix = "Starts On";
        } else if (key.endsWith("EndDate")) {
          suffix = "Ends On";
        } else if (key.endsWith("StartTimeZone")) {
          suffix = "Start Time Zone";
        } else if (key.endsWith("EndTimeZone")) {
          suffix = "End Time Zone";
        }

        baseName = baseName
          .replace(
            /\s*(Starts On|Ends On|Start|End|Start Timezone|End Timezone)\s*$/,
            ""
          )
          .trim();

        labels[key] = `${baseName} ${suffix}`;
      }
    });

    return labels;
  };

  const extractedLabels = extractLabels(filteredDates);

  const splitLabelsArray = (extractedLabels) => {
    const splitLabels = [];
    const dateKeys = Object.keys(extractedLabels).filter((key) =>
      key.endsWith("Date")
    );
    const processedKeys = new Set();

    dateKeys.forEach((currentDateKey) => {
      if (processedKeys.has(currentDateKey)) return;

      const currentTimeKey = currentDateKey.replace("Date", "Time");
      const currentLabel = extractedLabels[currentDateKey];

      const baseName = currentDateKey.replace(/StartDate|EndDate|Date/g, "");
      const isStartDate = currentDateKey.includes("StartDate");
      const isEndDate = currentDateKey.includes("EndDate");

      let pairedDateKey = "";
      let pairedTimeKey = "";
      let pairedLabel = "";

      if (isStartDate) {
        pairedDateKey = dateKeys.find(
          (key) => key.startsWith(baseName) && key.endsWith("EndDate")
        );
      } else if (isEndDate) {
        pairedDateKey = dateKeys.find(
          (key) => key.startsWith(baseName) && key.endsWith("StartDate")
        );
      }

      if (pairedDateKey && !processedKeys.has(pairedDateKey)) {
        pairedTimeKey = pairedDateKey.replace("Date", "Time");
        pairedLabel = extractedLabels[pairedDateKey];
        processedKeys.add(pairedDateKey);
      }

      splitLabels.push([
        currentDateKey,
        currentTimeKey,
        currentLabel,
        pairedDateKey || "",
        pairedTimeKey || "",
        pairedLabel || "",
      ]);

      processedKeys.add(currentDateKey);
    });

    return splitLabels;
  };

  const dateFields = splitLabelsArray(extractedLabels);

  return (
    <div className={styles.container}>
      <div>
        {dateFields &&
          Array.isArray(dateFields) &&
          dateFields.length > 0 &&
          dateFields
            .filter(([startDateKey]) => {
              if (!onlyShowDateKey) return true;
              return startDateKey === onlyShowDateKey;
            })
            .map(
              ([
                startDateKey,
                startTimeKey,
                startLabel,
                endDateKey,
                endTimeKey,
                endLabel,
              ]) => (
                <div
                  key={`${startDateKey}-${startTimeKey}-${endDateKey}-${endTimeKey}`}
                >
                  {renderDateTimeSet(
                    startDateKey,
                    startTimeKey,
                    startLabel,
                    endDateKey,
                    endTimeKey,
                    endLabel
                  )}
                </div>
              )
            )}
      </div>
    </div>
  );
}

DateV2Section.propTypes = {
  ruleset: PropTypes.string,
  location: PropTypes.string,
  eventName: PropTypes.string,
  onlyShowDateKey: PropTypes.string,
};
